import type { GameState } from '../../engine/game-state.js';
import { createGrid, CLASSIC_LAYOUT } from '../../render/classic/layout.js';
import type { ClassicSpec } from '../../render/classic/types.js';
import { CARD_W, CARD_H } from '../../render/classic/types.js';
import { drawFromStock, golfRules, hasAnyMatch, TABLEAU_IDS } from './rules.js';

// 7-column grid. Top row: stock + foundation flanking the centre column.
// Bottom row: 7 fan-down tableau columns.
const COLS = 7;
const TOP_Y = 24;
const TABLEAU_Y = TOP_Y + CARD_H + CLASSIC_LAYOUT.ROW_GAP;
const FELT_W = COLS * CARD_W + (COLS - 1) * CLASSIC_LAYOUT.COL_GAP + 48;
// Tableau columns are a fixed 5 cards and only ever shrink.
const FELT_H = TABLEAU_Y + CARD_H + (5 - 1) * CLASSIC_LAYOUT.FAN_DOWN + 24;

const layout = createGrid({
  cols: COLS,
  feltWidth: FELT_W,
  rows: [
    { y: TOP_Y, slots: [null, null, 'stock', null, 'foundation', null, null] },
    { y: TABLEAU_Y, fanDown: true, slots: [...TABLEAU_IDS] },
  ],
});

function playToFoundation(state: GameState, fromId: string, cardId: string): boolean {
  const move = { from: fromId, to: 'foundation', cardId };
  if (!golfRules.canMove(state, move)) return false;
  const fromPile = state.piles[fromId]!;
  const card = fromPile.cards.pop()!;
  state.piles.foundation!.cards.push(card);
  state.movesCount++;
  return true;
}

export const golfClassicSpec: ClassicSpec = {
  rules: golfRules,
  feltWidth: FELT_W,
  feltHeight: FELT_H,
  slotForPile: (id) => layout.slot(id),
  // No drag - every move is a click (tableau top or stock → foundation).
  dropExclude: () => [...TABLEAU_IDS, 'stock', 'foundation'],
  dragStackIds: () => [],
  autoSendOrder: () => ['foundation'],
  onPileClick(state, pile, cardId) {
    if (pile.id === 'stock') {
      if (!drawFromStock(state)) return false;
      state.movesCount++;
      return true;
    }
    if (pile.type === 'tableau') {
      return playToFoundation(state, pile.id, cardId);
    }
    return false;
  },
  statusText(state) {
    const stockEmpty = state.piles.stock!.cards.length === 0;
    if (stockEmpty && !hasAnyMatch(state) && !golfRules.isWon(state)) return 'No moves left';
    return null;
  },
  cascadeCardIds(state) {
    return state.piles.foundation!.cards.map((c) => c.id);
  },
  showSlotMarker: (id) => id === 'stock' || id === 'foundation',
};
